import { Locale } from "../Locale.ts";

/** The numberLine manipulative's labels (ADR 0117). */
const en = {
    "numberLine.lineLabel": "Number line from {min} to {max}",
    "numberLine.sliderLabel": "Point on the number line",
    "numberLine.negative": "negative {n}",
    "numberLine.pointAt": "Point at {value}",
    "numberLine.hopSize": "Hop size",
    "numberLine.hopSmaller": "Smaller hop",
    "numberLine.hopLarger": "Larger hop",
    "numberLine.hopRight": "Hop right",
    "numberLine.hopLeft": "Hop left",
    "numberLine.clearHops": "Clear hops",
    "numberLine.reset": "Reset",
    "numberLine.parts": "Parts in each whole",
    "numberLine.partsFewer": "Fewer parts",
    "numberLine.partsMore": "More parts",
    "numberLine.mixedNumbers": "Mixed numbers",
    "numberLine.roundTo": "Round to",
    "numberLine.nearest10": "Nearest 10",
    "numberLine.nearest100": "Nearest 100",
    "numberLine.roundingBetween": "{value} is between {lower} and {upper}. It rounds to {result}.",
    "numberLine.roundingHalfway":
        "{value} is halfway between {lower} and {upper}. A halfway number rounds up, to {result}.",
    "numberLine.roundingExact": "{value} is already a multiple of {unit}. It rounds to {result}.",
} as const;

export type NumberLineStringKey = keyof typeof en;

const es = {
    "numberLine.lineLabel": "Recta numérica de {min} a {max}",
    "numberLine.sliderLabel": "Punto en la recta numérica",
    "numberLine.negative": "menos {n}",
    "numberLine.pointAt": "El punto está en {value}",
    "numberLine.hopSize": "Tamaño del salto",
    "numberLine.hopSmaller": "Salto más corto",
    "numberLine.hopLarger": "Salto más largo",
    "numberLine.hopRight": "Saltar a la derecha",
    "numberLine.hopLeft": "Saltar a la izquierda",
    "numberLine.clearHops": "Borrar saltos",
    "numberLine.reset": "Reiniciar",
    "numberLine.parts": "Partes en cada entero",
    "numberLine.partsFewer": "Menos partes",
    "numberLine.partsMore": "Más partes",
    "numberLine.mixedNumbers": "Números mixtos",
    "numberLine.roundTo": "Redondear a",
    "numberLine.nearest10": "Decena más cercana",
    "numberLine.nearest100": "Centena más cercana",
    "numberLine.roundingBetween": "{value} está entre {lower} y {upper}. Se redondea a {result}.",
    "numberLine.roundingHalfway":
        "{value} está justo a la mitad entre {lower} y {upper}. Un número a la mitad se redondea hacia arriba, a {result}.",
    "numberLine.roundingExact": "{value} ya es múltiplo de {unit}. Se redondea a {result}.",
} satisfies Record<NumberLineStringKey, string>;

const fr = {
    "numberLine.lineLabel": "Droite numérique de {min} à {max}",
    "numberLine.sliderLabel": "Point sur la droite numérique",
    "numberLine.negative": "moins {n}",
    "numberLine.pointAt": "Le point est sur {value}",
    "numberLine.hopSize": "Taille du saut",
    "numberLine.hopSmaller": "Saut plus court",
    "numberLine.hopLarger": "Saut plus long",
    "numberLine.hopRight": "Sauter à droite",
    "numberLine.hopLeft": "Sauter à gauche",
    "numberLine.clearHops": "Effacer les sauts",
    "numberLine.reset": "Réinitialiser",
    "numberLine.parts": "Parts dans chaque entier",
    "numberLine.partsFewer": "Moins de parts",
    "numberLine.partsMore": "Plus de parts",
    "numberLine.mixedNumbers": "Nombres mixtes",
    "numberLine.roundTo": "Arrondir à",
    "numberLine.nearest10": "Dizaine la plus proche",
    "numberLine.nearest100": "Centaine la plus proche",
    "numberLine.roundingBetween": "{value} est entre {lower} et {upper}. Il s'arrondit à {result}.",
    "numberLine.roundingHalfway":
        "{value} est exactement au milieu de {lower} et {upper}. Un nombre au milieu s'arrondit au supérieur, à {result}.",
    "numberLine.roundingExact": "{value} est déjà un multiple de {unit}. Il s'arrondit à {result}.",
} satisfies Record<NumberLineStringKey, string>;

export const numberLineStrings: Record<Locale, Record<NumberLineStringKey, string>> = { en, es, fr };
